import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

/**
 * Checkout do PicPay numa janela sobre a loja.
 *
 * Location: src/components/checkout
 * Why: no modo Lightbox o jogador paga sem sair do site. A página é do PicPay
 * e vem num iframe — a Hystore não vê nada do que é digitado ali, que é o que
 * mantém a loja fora do escopo PCI-DSS mesmo com cartão.
 *
 * O que exige cuidado num iframe de terceiro:
 *
 * - **A URL é validada no backend** antes de chegar aqui (`hostDeCheckoutValido`).
 *   Este componente não confia no que recebe por confiar — confia porque o
 *   servidor já conferiu que o host é do PicPay.
 * - **`sandbox` no iframe**, liberando só o necessário. Sem isso a página
 *   embutida poderia navegar a janela de cima para fora da loja.
 * - **Foco preso** enquanto está aberto: quem usa teclado ou leitor de tela
 *   não pode tabular para trás e cair na loja atrás do pagamento.
 * - **Fechar não cancela o pagamento.** Quem confirma é o webhook, não esta
 *   janela. Por isso o aviso ao fechar, e não um "pagamento cancelado".
 */

export interface PicPayLightboxProps {
  url: string;
  onClose: () => void;
}

export function PicPayLightbox({ url, onClose }: PicPayLightboxProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const fecharRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    fecharRef.current?.focus();

    /*
     * Esc fecha, e o foco não escapa do modal.
     *
     * Sem prender o Tab, o jogador tabularia para os links da loja atrás da
     * janela de pagamento — a página continua ali, só coberta.
     */
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const focaveis = containerRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (!focaveis?.length) return;

      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];

      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    };

    document.addEventListener('keydown', aoTeclar);

    // A página de trás não deve rolar enquanto o pagamento está aberto.
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = overflowAnterior;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('store.cart.lightbox_title')}
    >
      <div
        ref={containerRef}
        className="relative flex h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-card shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">
            {t('store.cart.lightbox_title')}
          </h2>
          <button
            ref={fecharRef}
            type="button"
            onClick={onClose}
            aria-label={t('store.cart.lightbox_close')}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/*
          `sandbox` libera só o que a página de pagamento precisa.
          `allow-top-navigation` fica **de fora** de propósito: sem ele, a
          página embutida não consegue levar a janela da loja para outro lugar.
        */}
        <iframe
          src={url}
          title={t('store.cart.lightbox_title')}
          className="h-full w-full flex-1 border-0"
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
        />

        <p className="border-t border-border px-4 py-2 text-center text-xs text-muted-foreground">
          {t('store.cart.lightbox_note')}
        </p>
      </div>
    </div>
  );
}
